import React, { useState, useEffect } from "react";
import { Activity, Hand, Dumbbell, Sparkles, Brain, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Activity: Activity,
  Hand: Hand,
  Dumbbell: Dumbbell,
  Sparkles: Sparkles,
  Brain: Brain,
};

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
        // Fallback data
        setServices([
          {
            id: "physiotherapy",
            name: "Physiotherapy",
            description: "Complete rehabilitation including education, exercise and manual therapy.",
            features: ["Traction Therapy", "Manual Therapy", "Laser Therapy", "Electrotherapy"],
            icon: "Activity",
            color: "#0F4C3A",
            image: "https://images.unsplash.com/photo-1540205895360-4ad4cffb3aa8?w=800&q=80&auto=format&fit=crop"
          },
          {
            id: "massage",
            name: "Massage Therapy",
            description: "Safe and natural treatment to help you recover faster and relieve pain.",
            features: ["Deep Tissue", "Myofascial Release", "Trigger Point Release"],
            icon: "Hand",
            color: "#0F4C3A",
            image: "https://images.unsplash.com/photo-1519824145371-296894a0daa9?w=800&q=80&auto=format&fit=crop"
          },
          {
            id: "kinesiology",
            name: "Kinesiology",
            description: "Functional approach to recovery through active rehabilitation.",
            features: ["Exercise Therapy", "Strength Training", "Postural Analysis"],
            icon: "Dumbbell",
            color: "#0F4C3A",
            image: "https://images.unsplash.com/photo-1522898467493-49726bf28798?w=800&q=80&auto=format&fit=crop"
          },
          {
            id: "acupuncture",
            name: "Acupuncture",
            description: "Ancient Chinese technique to treat pain and improve wellbeing.",
            features: ["Pain Relief", "Stress Reduction", "Recovery Support"],
            icon: "Sparkles",
            color: "#0F4C3A",
            image: "https://images.unsplash.com/photo-1598555763574-dca77e10427e?w=800&q=80&auto=format&fit=crop"
          }
        ]);
      }
    };
    fetchServices();
  }, []);

  // Filter out HeadWorks as it has its own section
  const regularServices = services.filter(s => s.id !== "headworks");

  return (
    <section
      id="services"
      className="py-20 md:py-28 bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#0F4C3A] font-semibold text-sm uppercase tracking-wider">
            Our Services
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            Comprehensive Care for{" "}
            <span className="text-[#0F4C3A]">Every Need</span>
          </h2>
          <p className="mt-6 text-lg text-gray-600">
            From injury recovery to preventive care, our team of certified professionals 
            provides personalized treatment plans tailored to your unique goals.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {regularServices.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Activity;
            return (
              <div
                key={service.id}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#0F4C3A]/20 transition-all duration-300 card-hover overflow-hidden flex flex-col"
                data-testid={`service-card-${service.id}`}
              >
                {/* Themed Image */}
                {service.image && (
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={service.image}
                      alt={`${service.name} at BC-ICE`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                    {/* Icon badge */}
                    <div
                      className="absolute bottom-3 left-3 w-11 h-11 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-md bg-white/90"
                      style={{ color: service.color }}
                    >
                      <IconComponent className="w-5 h-5" />
                    </div>
                  </div>
                )}

                {/* Body */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0F4C3A] transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#0F4C3A]"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <a
                    href={`#${service.id}`}
                    className="mt-auto inline-flex items-center gap-2 text-[#0F4C3A] font-semibold text-sm group-hover:gap-3 transition-all"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <a
            href="https://bc-ice.janeapp.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0F4C3A] text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:bg-[#1A6B52] hover:shadow-xl transition-all hover:-translate-y-1"
            data-testid="services-book-btn"
          >
            Book Your Service
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
